import { isNonEmpty } from 'fp-ts/Array';
import { number } from 'fp-ts';
import {
    Account,
    AccountType,
    AgeCategory,
    OrderItem,
    Product,
    ProductChecklistResult,
    ProductServiceOrder,
    Reservation,
    ServiceOrder,
} from '@mews-commander/data-access';
import { compactArray, keyById } from '@mews/fp';
import {
    getOrderItemMultiplier,
    isOrderItemProductOrder,
} from '@mews-commander/legacy-entities/src/ducks/orderItemNew';
import { isProductServiceOrder, isReservation } from '@mews-commander/legacy-entities/src/ducks/serviceOrderNew';
import { ProductCheckListDataRow, ProductData } from '../types';
import { getAgeCategoryIdentifier } from './ageCategories';

export interface ProductCheckListData {
    rows: ProductCheckListDataRow[];
    totalRowCount: number;
    ageCategories: AgeCategory[];
}

export const productChecklistResultToProductCheckListData = (
    originalData: ProductChecklistResult
): ProductCheckListData => {
    const orderItemsById = isNonEmpty(originalData.orderItems) ? keyById(originalData.orderItems) : null;
    const accountsById = isNonEmpty(originalData.accounts) ? keyById(originalData.accounts) : null;
    const serviceOrdersById = isNonEmpty(originalData.serviceOrders) ? keyById(originalData.serviceOrders) : null;
    const productsById = isNonEmpty(originalData.products) ? keyById(originalData.products) : null;

    const rows = originalData.productChecklistData.map((dailyProductData, index): ProductCheckListDataRow => {
        const { assignedResourceId, orderItemIds, ownerId, serviceOrderId, companionIds } = dailyProductData;
        const serviceOrder = serviceOrdersById?.[serviceOrderId];

        const notes = originalData.notes.filter((n) => n.serviceOrderId === serviceOrderId);

        const productsData = getProductsData(
            orderItemIds,
            orderItemsById,
            productsById,
            originalData.ageCategories,
            serviceOrder
        ).sort((a, b) => number.Ord.compare(a.data.ordering, b.data.ordering));

        const someNotClaimed = productsData.some(({ totalCount, claimed }) => totalCount !== claimed);

        return {
            ...getGuestsAndOwner(ownerId, companionIds, accountsById),
            ...getServiceOrderData(serviceOrder, accountsById),
            notes,
            productsData,
            serviceId: serviceOrder?.serviceId,
            allClaimed: !!productsData.length && !someNotClaimed,
            id: serviceOrder?.data?.value.id ?? index.toString(),
            resourceId: assignedResourceId,
        };
    });

    return {
        totalRowCount: originalData.totalRowCount,
        ageCategories: originalData.ageCategories,
        rows,
    };
};

const getProductsData = (
    orderItemIds: string[],
    orderItemsById: Record<string, OrderItem> | null,
    productsById: Record<string, Product> | null,
    ageCategories: AgeCategory[],
    serviceOrder: ServiceOrder | null | undefined
): ProductData[] => {
    const products = orderItemIds.reduce(
        (acc, orderItemId) => {
            const orderItem = orderItemsById?.[orderItemId];

            const { productId = undefined, ageCategoryId = undefined } =
                orderItem && isOrderItemProductOrder(orderItem) ? orderItem.data.value : {};

            const product = productId && productsById?.[productId];

            if (!product || !orderItem) {
                return acc;
            }

            const ageCategoryIdOrDefaultAgeCategoryId =
                serviceOrder && isProductServiceOrder(serviceOrder)
                    ? null
                    : ageCategoryId ||
                      ageCategories.find(
                          (ageCategory) => ageCategory.serviceId === serviceOrder?.serviceId && ageCategory.isDefault
                      )?.id;

            const ageCategoryIdentifier = getAgeCategoryIdentifier(ageCategoryIdOrDefaultAgeCategoryId);

            const emptyProductData: ProductData = {
                data: product,
                totalCount: 0,
                claimed: 0,
                claimedByAgeCategories: {},
                orderItems: [],
            };

            const claimedIncrease = orderItem.claimedCount;

            const productToAdd = acc[product.id] || emptyProductData;

            const claimedByAgeCategories = productToAdd.claimedByAgeCategories[ageCategoryIdentifier] ?? {
                total: 0,
                claimed: 0,
            };

            productToAdd.orderItems.push(orderItem);

            return {
                ...acc,
                [product.id]: {
                    ...productToAdd,
                    totalCount: productToAdd.totalCount + getOrderItemMultiplier(orderItem),
                    claimed: productToAdd.claimed + claimedIncrease,
                    claimedByAgeCategories: {
                        ...productToAdd.claimedByAgeCategories,
                        [ageCategoryIdentifier]: {
                            total: claimedByAgeCategories.total + getOrderItemMultiplier(orderItem),
                            claimed: claimedByAgeCategories.claimed + claimedIncrease,
                        },
                    },
                },
            };
        },
        {} as Record<string, ProductData>
    );

    return Object.values(products);
};

const getServiceOrderData = (
    serviceOrder: ServiceOrder | undefined,
    accountsById: Record<string, Account> | null
): { reservation: Reservation | undefined; productServiceOrder: ProductServiceOrder | undefined } => {
    if (!serviceOrder) {
        return { reservation: undefined, productServiceOrder: undefined };
    }

    const isServiceOrderReservation = isReservation(serviceOrder);
    const reservation = isServiceOrderReservation ? serviceOrder.data.value : undefined;

    if (isServiceOrderReservation && reservation) {
        const { ownerId } = reservation;

        const customerAccount = ownerId ? accountsById?.[ownerId] : undefined;

        const customer =
            customerAccount?.data.discriminator === AccountType.Customer ? customerAccount.data.value : undefined;

        return { reservation: { ...reservation, customer }, productServiceOrder: undefined };
    }

    if (isProductServiceOrder(serviceOrder)) {
        return { reservation: undefined, productServiceOrder: serviceOrder.data.value };
    }

    return { reservation: undefined, productServiceOrder: undefined };
};

const getGuestsAndOwner = (
    ownerId: string,
    companionIds: string[] | null | undefined,
    accountsById: Record<string, Account> | null
) => ({
    owner: accountsById?.[ownerId],
    guests:
        companionIds && accountsById ? compactArray(companionIds.map((companionId) => accountsById[companionId])) : [],
});
